import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

// Base schemas
export const ApiTokenSchema = z.object({
  tokenId: PrimaryKeySchema,
  teamId: PrimaryKeySchema,
  name: z.string().min(1),
  tokenPrefix: z.string(),
  last4: z.string().length(4),
  role: z.enum(['admin', 'member', 'readonly']),
  createdByUserId: PrimaryKeySchema,
  expiresAt: z.coerce.date().nullable(),
  lastUsedAt: z.coerce.date().nullable(),
  isActive: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type ApiToken = z.infer<typeof ApiTokenSchema>;

export const ApiTokensSchema = z.array(ApiTokenSchema);

export type ApiTokens = z.infer<typeof ApiTokensSchema>;

// API token usage schema
export const ApiTokenUsageSchema = z.object({
  usageId: PrimaryKeySchema,
  tokenId: PrimaryKeySchema,
  endpoint: z.string(),
  method: z.string(),
  statusCode: z.number(),
  responseTimeMs: z.number(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.coerce.date(),
});

export type ApiTokenUsage = z.infer<typeof ApiTokenUsageSchema>;

export const ApiTokenUsagesSchema = z.array(ApiTokenUsageSchema);

export type ApiTokenUsages = z.infer<typeof ApiTokenUsagesSchema>;

// Create/Update schemas
export const CreateApiTokenRequestSchema = z.object({
  teamId: PrimaryKeySchema,
  name: z.string().min(1),
  role: z.enum(['admin', 'member', 'readonly']),
  expiresAt: z.coerce.date().optional(),
});

export type CreateApiTokenRequest = z.infer<typeof CreateApiTokenRequestSchema>;

export const UpdateApiTokenRequestSchema = z.object({
  name: z.string().min(1).optional(),
  isActive: z.boolean().optional(),
  expiresAt: z.coerce.date().nullable().optional(),
});

export type UpdateApiTokenRequest = z.infer<typeof UpdateApiTokenRequestSchema>;

// Response schema for token creation (includes plaintext token)
export const CreateApiTokenResponseSchema = z.object({
  token: z.string(),
  apiToken: ApiTokenSchema,
});

export type CreateApiTokenResponse = z.infer<
  typeof CreateApiTokenResponseSchema
>;

// Filter schemas
export const ApiTokenFiltersSchema = z
  .object({
    tokenIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    teamId: PrimaryKeySchema.optional(),
    isActive: z.boolean().optional(),
    createdByUserId: PrimaryKeySchema.optional(),
  })
  .merge(
    createOffsetPaginationRequestQuerySchema(z.enum(['createdAt', 'name'])),
  );

export type ApiTokenFilters = z.infer<typeof ApiTokenFiltersSchema>;

export const ApiTokenUsageFiltersSchema = z
  .object({
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
    endpoint: z.string().optional(),
    method: z.string().optional(),
  })
  .merge(createOffsetPaginationRequestQuerySchema(z.enum(['createdAt'])));

export type ApiTokenUsageFilters = z.infer<typeof ApiTokenUsageFiltersSchema>;

// Usage stats schema
export const ApiTokenUsageStatsSchema = z.object({
  totalRequests: z.number(),
  requestsByEndpoint: z.record(z.string(), z.number()),
  requestsByMethod: z.record(z.string(), z.number()),
  averageResponseTime: z.number(),
  errorRate: z.number(),
});

export type ApiTokenUsageStats = z.infer<typeof ApiTokenUsageStatsSchema>;

// The filters already include pagination, so we just export them
export type GetApiTokensRequest = ApiTokenFilters;
export type GetApiTokenUsagesRequest = ApiTokenUsageFilters;
